import { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/auth";
import { usePreferencesStore } from "@/stores/preferences";
import { TierBadge } from "@/components/GatedFeature";
import { TourCoach } from "@/components/product-tour/TourCoach";
import { UpdateBanner } from "@/components/UpdateBanner";
import { OrgBanner } from "@/components/OrgBanner";
import { IOSInstallBanner } from "@/components/IOSInstallBanner";
import { PushOnboardingBanner } from "@/components/PushOnboardingBanner";
import { Toaster, toast } from "sonner";
import {
  Calendar,
  CalendarClock,
  Compass,
  ChevronRight,
  FileText,
  Globe,
  Home,
  LogOut,
  Music,
  Palette,
  Settings,
  Shield,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { initialsFor } from "@/lib/orgColor";
import { Icon } from "@/components/ds";
import { BrandActions } from "@/components/shell/BrandActions";
import { OrgSwitcher } from "@/components/shell/OrgSwitcher";
import { TopBar } from "@/components/shell/TopBar";
import { SupportDialog } from "@/components/support/SupportDialog";
import { MobileAppShell } from "@/layouts/MobileAppShell";
import { useIsMobileViewport } from "@/hooks/useMediaQuery";
import { shouldShowOrgBanner } from "@/lib/orgBannerVisibility";

type Feature = "upload_rider" | "crew_calculator";

const artistNavItems = [{ to: "/my-schedule", label: "My Schedule", icon: Music }];

const productionNavItems: {
  to: string;
  label: string;
  icon: LucideIcon;
  feature?: Feature;
}[] = [
  { to: "/dashboard", label: "Dashboard", icon: Home },
  { to: "/festivals", label: "Festivals", icon: Calendar },
  { to: "/riders", label: "Riders", icon: FileText, feature: "upload_rider" },
  { to: "/crew", label: "Crew", icon: Users, feature: "crew_calculator" },
];

/** Sidebar nav row with animated accent rail. */
function NavItem({
  to,
  label,
  icon,
  active,
  external,
  trailing,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active?: boolean;
  external?: boolean;
  trailing?: React.ReactNode;
  onNavigate?: () => void;
}) {
  const inner = (
    <>
      <span className="nav-item__rail" />
      <span className="nav-item__icon">
        <Icon icon={icon} size={16} />
      </span>
      <span className="nav-item__label">{label}</span>
      {trailing}
    </>
  );
  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={cn("nav-item", active && "nav-item--active")}
      >
        {inner}
      </a>
    );
  }
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn("nav-item", active && "nav-item--active")}
    >
      {inner}
    </Link>
  );
}

export function AppLayout() {
  const isMobile = useIsMobileViewport();
  return isMobile ? <MobileAppShell /> : <DesktopAppShell />;
}

function DesktopAppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [productionCollapsed, setProductionCollapsed] = useState(
    !!user?.has_artist_profile,
  );
  const [showProductionInfo, setShowProductionInfo] = useState(false);
  const { hideProduction, productionInfoSeen, setHideProduction, setProductionInfoSeen } =
    usePreferencesStore();

  // Close mobile sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Refresh failed");
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };

  const isActive = (to: string, items: { to: string }[] = []) => {
    const matches = location.pathname === to || location.pathname.startsWith(to + "/");
    if (!matches) return false;
    // a more-specific sibling owns the highlight
    const moreSpecific = items.some(
      (n) =>
        n.to !== to &&
        n.to.startsWith(to + "/") &&
        (location.pathname === n.to || location.pathname.startsWith(n.to + "/")),
    );
    return !moreSpecific;
  };

  const sidebarContent = (
    <>
      {/* Brand */}
      <div className="flex items-start justify-between px-[18px] pb-3.5 pt-5">
        <div>
          <img
            src="/trace-logo.svg"
            alt="TRACE"
            className="brand__logo h-[18px]"
            style={{ filter: "var(--logo-invert)" }}
          />
          <p className="brand__tag mt-1.5">Festival Intelligence</p>
        </div>
        <div className="flex items-center gap-1">
          <BrandActions />
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="iconbtn md:hidden"
            aria-label="Close menu"
          >
            <Icon icon={X} size={16} />
          </button>
        </div>
      </div>

      {/* Org switcher */}
      <div className="px-3 pb-3.5">
        <OrgSwitcher />
      </div>

      {/* Nav */}
      <nav className="nav flex-1 overflow-y-auto px-3">
        {/* Artist — only for users with an artist profile */}
        {user?.has_artist_profile && (
          <div className="nav-group">
            <p className="nav-group__label">Artist</p>
            {artistNavItems.map((it) => (
              <NavItem
                key={it.to}
                to={it.to}
                label={it.label}
                icon={it.icon}
                active={isActive(it.to)}
              />
            ))}
          </div>
        )}

        {/* Production — collapsible, hideable for artists */}
        {!(hideProduction && user?.has_artist_profile) && (
          <div className="nav-group">
            <button
              type="button"
              onClick={() => {
                if (productionCollapsed && user?.has_artist_profile && !productionInfoSeen) {
                  setShowProductionInfo(true);
                } else {
                  setProductionCollapsed(!productionCollapsed);
                }
              }}
              className="nav-group__label flex w-full items-center gap-1 hover:text-ink-3"
            >
              <ChevronRight
                size={11}
                strokeWidth={2}
                className={cn("transition-transform", !productionCollapsed && "rotate-90")}
              />
              Production
            </button>
            {!productionCollapsed &&
              productionNavItems.map((it) => (
                <NavItem
                  key={it.to}
                  to={it.to}
                  label={it.label}
                  icon={it.icon}
                  active={isActive(it.to, productionNavItems)}
                  trailing={it.feature ? <TierBadge feature={it.feature} /> : undefined}
                />
              ))}
          </div>
        )}

        {/* Public */}
        <div className="nav-group">
          <p className="nav-group__label">Public</p>
          <NavItem to="/live" label="Discover" icon={Globe} external />
        </div>

        <div className="nav-div" />

        <NavItem to="/learn" label="Explore Trace" icon={Compass} active={isActive("/learn")} />

        {/* Footer items */}
        <NavItem
          to="/my-shifts"
          label="My Shifts"
          icon={CalendarClock}
          active={isActive("/my-shifts")}
        />
        {user?.is_superadmin && (
          <NavItem
            to="/design-system"
            label="Design System"
            icon={Palette}
            active={isActive("/design-system")}
          />
        )}
        <NavItem
          to="/settings"
          label="Settings"
          icon={Settings}
          active={isActive("/settings")}
        />
        {user?.is_superadmin && (
          <NavItem to="/admin" label="Admin" icon={Shield} active={isActive("/admin")} />
        )}
      </nav>

      {/* User chip */}
      <div className="flex items-center gap-2.5 border-t border-line-2 px-4 py-3">
        <span className="userav grid h-[30px] w-[30px] place-items-center rounded-lg bg-panel-3 text-[11px] font-bold text-ink-2">
          {initialsFor(user?.name || user?.email)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[12.5px] font-semibold text-ink">
            {user?.name || user?.email || "User"}
          </p>
          <p className="truncate text-[10.5px] capitalize text-ink-3">
            {user?.effective_tier || user?.subscription_status}
          </p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="iconbtn"
          aria-label="Log out"
          title="Log out"
        >
          <Icon icon={LogOut} size={16} />
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-bg text-ink">
      {/* Desktop sidebar */}
      <aside className="hidden w-[248px] flex-shrink-0 flex-col border-r border-line-2 bg-sidebar md:flex">
        {sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-line-2 bg-sidebar transition-transform duration-200 md:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
        )}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {sidebarContent}
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar
          onMenu={() => setSidebarOpen(true)}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
        <UpdateBanner />
        <TourCoach />
        <main className="ds-scroll flex-1 overflow-auto">
          <div className="mx-auto w-full max-w-[1520px] px-4 py-5 md:px-6 md:py-6">
            {shouldShowOrgBanner(location.pathname) && <OrgBanner />}
            <Outlet />
          </div>
        </main>
      </div>

      <IOSInstallBanner />
      {/* Everyone onboards with notifications: shown until activated,
          dismissed, or explicitly denied. */}
      {user && <PushOnboardingBanner />}
      <Toaster theme="dark" position="bottom-right" />
      <SupportDialog />

      {/* Production info modal for artists */}
      {showProductionInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-sm space-y-4 p-6">
            <h3 className="text-lg font-semibold">Production tools</h3>
            <p className="text-sm leading-relaxed text-ink-2">
              This section gives you access to festival management, rider uploads, and
              production intelligence. These are tools typically used by production teams.
              You can hide this section if you only need the Artist view.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setProductionInfoSeen();
                  setProductionCollapsed(false);
                  setShowProductionInfo(false);
                }}
                className="btn btn--ghost btn--sm"
              >
                Keep visible
              </button>
              <button
                type="button"
                onClick={() => {
                  setHideProduction(true);
                  setProductionInfoSeen();
                  setShowProductionInfo(false);
                }}
                className="btn btn--accent btn--sm"
              >
                Hide production
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
